
import {
    DefaultButton,
    Dropdown,
    IDropdownOption,
    IPanelProps,
    IRenderFunction,
    Label,
    Panel,
    PanelType,
    PrimaryButton,
    Separator,
    Stack
} from "@fluentui/react";
import { useId } from '@fluentui/react-hooks';
import { TagPicker } from '@fluentui/react/lib/Pickers';
import { RootState } from "app/store";
import Costshare, { IItemTag } from 'components/controls/Costshare';
import { EControlType } from "components/ControlWrapper";
import { fadeIn, panelButtonsStyles } from "global/Application";
import { addUpdateBenefit, changeBulkCostshare } from "global/Reducer/dataSlice";
import { endSnapshotLoad, startSnapshotLoad } from "global/Reducer/statusSlice";
import { Delay } from "global/Utility/TSFunctions";
import * as FAM from "model/FlexibleAttributeModel";
import { useMemo, useState } from "react";
import { connect, ConnectedProps, useDispatch } from "react-redux";


export type TCostshareWizardVariant = "searchAndReplace" | "defaults";
type TTag = IItemTag<FAM.IEntityReferenceValue>;

interface IProps extends TComponentConnectedProps {
    isOpen: boolean;
    onCancel?: () => void;
    onDismiss: () => void;
    currentBenefit?: FAM.IBenefit
    variant: TCostshareWizardVariant;
}

interface ICostshareBulkUpdateTiers extends FAM.ITierLevel {
    primaryValue?: FAM.IEntityReferenceValue;
    pcpValue?: FAM.IEntityReferenceValue;
    specialistValue?: FAM.IEntityReferenceValue;
}

type TSearchContext = 'benefit' | 'nonstandard' | 'attributeSet';

const BulkCostShareWizard = (props: IProps) => {
    const dispatch = useDispatch();
    const pickerId = useId('custom-icon-picker');

    const [costshareDefaultTiers, setCostshareDefaultTiers] = useState<ICostshareBulkUpdateTiers[]>(props.currentBenefit?.tiers ?? []);
    const [searchReplaceValue, setSearchReplaceValue] = useState({
        search: null as (FAM.IEntityReferenceValue | null),
        replace: null as (FAM.IEntityReferenceValue | null),
        searchContext: ['benefit', 'nonstandard', 'attributeSet'] as TSearchContext[],
    });

    const optionsSearchContext: IDropdownOption[] = useMemo(() => {
        return [
            { key: 'benefit', text: 'Benefit' },
            { key: 'nonstandard', text: 'Non Standards' },
            { key: 'attributeSet', text: 'Attribute Sets' },
        ];
    }, []);

    const configMasterAttributes = props.state.data.config.masterattributes

    const getMatch = (a: any, b: any) => {
        let matches = [];
        for (let i = 0; i < a.length; i++) {
            for (let e = 0; e < b.length; e++) {
                if (a[i].masterAttributeID === b[e].bcbs_config_masterattributeid)
                    matches.push(a[i]);
            }
        }
        return matches;
    }

    const filterCSMasterAttributes = () => {
        const attrType = (field: any) => {
            return configMasterAttributes.find(({ bcbs_config_masterattributeid, bcbs_isacceptsdefaultvalue }) => bcbs_config_masterattributeid === field.masterAttributeID && bcbs_isacceptsdefaultvalue)
        }

        const filtered = props.currentBenefit?.fields?.map((field) => attrType(field))
        return filtered?.filter(f => {
            if (f)
                return f
        })
    }

    const benefitCostShares = useMemo(() => {
        const discoveredCostShares = new Map<string, TTag>();
        if (props.isOpen) {

            const discoverCostShares = (fields: FAM.IFlexibleField[]) => {

                fields.forEach(eField => {

                    const attribute = props.state.data.config.cache.map_bcbs_config_attribute[eField.configRef.id];

                    if (!attribute) {
                        console.warn("Missing attribute", eField.configRef.id)
                        return;
                    }

                    const masterAttribute = props.state.data.config.cache.map_bcbs_config_masterattribute[attribute._bcbs_config_masterattributeid_value!];

                    if (masterAttribute.bcbs_attributetypecode === EControlType.costshare) {
                        eField.values.forEach(value => {
                            const refValue = value.value as FAM.IEntityReferenceValue;
                            if (refValue) {
                                discoveredCostShares.set(refValue.id, {
                                    key: refValue.id,
                                    name: refValue.name,
                                    search: refValue.name.toLowerCase(),
                                    data: refValue
                                })
                            }
                        })
                    }
                });
            }
            if (props.currentBenefit) {

                discoverCostShares(props.currentBenefit.fields);

                if (props.currentBenefit?.nonStandards?.length ?? 0 > 0)
                    props.currentBenefit.nonStandards.forEach(eachNS => {
                        discoverCostShares(eachNS.fields);
                    });

                if (props.currentBenefit?.attributeSets?.length ?? 0 > 0)
                    props.currentBenefit.attributeSets.forEach(eachAS => {
                        discoverCostShares(eachAS.fields);
                    });

            }
        }

        return [...discoveredCostShares.values()];
    }, [props.isOpen])

    const searchAndReplace = async () => {
        try {
            // this is to trigger remount, since we are not doing a controlled components
            dispatch(startSnapshotLoad());

            const currentBenefitCloned = structuredClone(props.currentBenefit)!;

            const replaceCostShares = (fields: FAM.IFlexibleField[]) => {

                fields.forEach(eField => {

                    const config = props.state.data.config.cache.map_bcbs_config_attribute[eField.configRef.id];
                    const configMaster = props.state.data.config.cache.map_bcbs_config_masterattribute[config._bcbs_config_masterattributeid_value!];

                    if (configMaster.bcbs_attributetypecode === EControlType.costshare) {
                        eField.values.forEach(value => {
                            const refValue = value.value as FAM.IEntityReferenceValue;
                            if (refValue) {
                                if (refValue.id === searchReplaceValue.search?.id)
                                    value.value = searchReplaceValue.replace!;
                            }
                        })
                    }
                });
            }

            if (searchReplaceValue.searchContext.includes('benefit'))
                replaceCostShares(currentBenefitCloned.fields);

            if (searchReplaceValue.searchContext.includes('nonstandard'))
                if (currentBenefitCloned?.nonStandards?.length ?? 0 > 0)
                    currentBenefitCloned.nonStandards.forEach(eachNS => replaceCostShares(eachNS.fields));

            if (searchReplaceValue.searchContext.includes('attributeSet'))
                if (currentBenefitCloned?.attributeSets?.length ?? 0 > 0)
                    currentBenefitCloned.attributeSets.forEach(eachAS => replaceCostShares(eachAS.fields));

            dispatch(addUpdateBenefit([currentBenefitCloned]));

            await Delay(0)
            props.onDismiss();

        } finally {
            dispatch(endSnapshotLoad());
        }

    }

    const updateCostshareDefaults = async () => {
        try {
            dispatch(startSnapshotLoad());
            const costShareFields = getMatch(props.currentBenefit?.fields, filterCSMasterAttributes()).filter(cs => cs.values.length > 1)
            dispatch(changeBulkCostshare({ costshareFields: costShareFields, costshareValues: costshareDefaultTiers, currentBenefit: props.currentBenefit }))
            await Delay(0)
            props.onDismiss();
        } finally {

            dispatch(endSnapshotLoad());
        }

    }


    const onRenderDefault: IRenderFunction<IPanelProps> = (scopeProps, defaultRender) => {
        return <>
            {
                costshareDefaultTiers.map((t, index) =>
                    <div id={t.id} key={t.id}>
                        <Stack>
                            <h3>{t.name}</h3>
                            {t.name !== 'Out of Network' ?
                                <Stack >
                                    <Costshare
                                        label='General Coinsurance'
                                        controlType={EControlType.costshare}
                                        category={props.currentBenefit}
                                        fieldValueOnChange={(event: any, value: FAM.IEntityReferenceValue | null) => {
                                            if (!value)
                                                return;

                                            const w = costshareDefaultTiers.map((q, qIndex) => {
                                                if (qIndex === index) {
                                                    const q2: ICostshareBulkUpdateTiers = { ...q, primaryValue: value }
                                                    return q2
                                                }
                                                return q
                                            })
                                            setCostshareDefaultTiers(w)
                                        }}
                                    />
                                    <Costshare
                                        label='PCP'
                                        controlType={EControlType.costshare}
                                        category={props.currentBenefit}
                                        fieldValueOnChange={(event: any, value: FAM.IEntityReferenceValue | null) => {
                                            if (!value)
                                                return;

                                            const w = costshareDefaultTiers.map((q, qIndex) => {
                                                if (qIndex === index) {
                                                    const q2: ICostshareBulkUpdateTiers = { ...q, pcpValue: value }
                                                    return q2
                                                }
                                                return q
                                            })
                                            setCostshareDefaultTiers(w)
                                        }}

                                    />
                                    <Costshare
                                        label='Specialist'
                                        controlType={EControlType.costshare}
                                        category={props.currentBenefit}
                                        fieldValueOnChange={(event: any, value: FAM.IEntityReferenceValue | null) => {
                                            if (!value)
                                                return;

                                            const w = costshareDefaultTiers.map((q, qIndex) => {
                                                if (qIndex === index) {
                                                    const q2: ICostshareBulkUpdateTiers = { ...q, specialistValue: value }
                                                    return q2
                                                }
                                                return q
                                            })
                                            setCostshareDefaultTiers(w)
                                        }}

                                    />
                                </Stack> :
                                <Stack>
                                    <Costshare
                                        label='General Coinsurance'
                                        controlType={EControlType.costshare}
                                        category={props.currentBenefit}
                                        fieldValueOnChange={(event: any, value: FAM.IEntityReferenceValue | null) => {
                                            if (!value)
                                                return;

                                            const w = costshareDefaultTiers.map((q, qIndex) => {
                                                if (qIndex === index) {
                                                    const q2: ICostshareBulkUpdateTiers = { ...q, primaryValue: value }
                                                    return q2
                                                }
                                                return q
                                            })
                                            setCostshareDefaultTiers(w)
                                        }}

                                    />
                                </Stack>}
                        </Stack>
                    </div>
                )
            }
        </>

    }

    const onRenderDefaultFooter: IRenderFunction<IPanelProps> = (scopeProps, defaultRender) => {
        return <div className={fadeIn}>
            <PrimaryButton text='Update'
                styles={panelButtonsStyles}
                onClick={() => updateCostshareDefaults()}
            />
            <DefaultButton text='Cancel' onClick={() => props.onDismiss?.()} />
        </div>
    }

    const onRenderSearchReplace: IRenderFunction<IPanelProps> = (scopeProps, defaultRender) => {
        return <div className={fadeIn}>
            <Label>Select Existing Costshare</Label>

            {/* TODO replace this with a control\costshare; control needs external option inject overload */}
            <TagPicker
                onResolveSuggestions={(filter, selected) => {
                    const filtered = benefitCostShares.filter(t => {
                        if (t.search)
                            return t.search.indexOf(filter) > -1;

                        return true;
                    });
                    return filtered;
                }}
                onChange={(items) => {
                    const data = items && items.length > 0
                        ? (items[0] as TTag).data ?? null
                        : null;

                    setSearchReplaceValue(t => {
                        return {
                            ...t,
                            search: data ?? null
                        }
                    });

                }}

                // getTextfromItem={(item: ITag) => item.name}
                // pickerSuggestionsProps={pickerSuggestionProps}
                removeButtonIconProps={{ iconName: 'Delete' }}
                itemLimit={1}
                pickerCalloutProps={{ doNotLayer: true }}
                inputProps={{ id: pickerId, }}
            // onChange={(event: any, value: FAM.IEntityReferenceValue) =>
            //     setExistingValue(value)  //on change, set existing costshare value
            // }
            />

            <Costshare
                label='Replace'
                controlType={EControlType.costshare}
                category={props?.currentBenefit}
                fieldValueOnChange={(event: any, value: FAM.IEntityReferenceValue | null) => {
                    if (!value)
                        return;

                    setSearchReplaceValue(t => { return { ...t, replace: value } })
                }}
            />

            <Dropdown
                label="Context"
                selectedKeys={searchReplaceValue.searchContext}
                // eslint-disable-next-line react/jsx-no-bind
                onChange={(event, option, index) => {
                    if (option) {

                        setSearchReplaceValue(t => {
                            return {
                                ...t,
                                searchContext: option.selected
                                    ? [...t.searchContext, option.key as TSearchContext]
                                    : t.searchContext.filter(key => key !== option.key)
                            };
                        });
                    }
                }}
                multiSelect
                options={optionsSearchContext}
            />

        </div>;
    }

    const onRenderSearchReplaceFooter: IRenderFunction<IPanelProps> = (scopeProps, defaultRender) => {
        return <div className={fadeIn}>
            <PrimaryButton text='Replace'
                disabled={!searchReplaceValue || !searchReplaceValue.replace || !searchReplaceValue.search}
                styles={panelButtonsStyles}
                onClick={() => searchAndReplace()}
            />
            <DefaultButton text='Cancel' onClick={() => props.onDismiss?.()} />
        </div>;
    }

    const onRenderUnknown: IRenderFunction<IPanelProps> = (scopeProps, defaultRender) => {
        return <div>Unknown variant.</div>;
    }

    const onRenderFooter: IRenderFunction<IPanelProps> = (scopeProps, defaultRender) => {
        let onRender = onRenderUnknown;

        switch (props.variant) {
            case 'defaults':
                onRender = onRenderDefaultFooter
                break;

            case 'searchAndReplace':
                onRender = onRenderSearchReplaceFooter
                break;
        }

        return onRender();
    }

    const onRenderBody: IRenderFunction<IPanelProps> = (scopeProps, defaultRender) => {
        let onRender = onRenderUnknown;

        switch (props.variant) {
            case 'defaults':
                onRender = onRenderDefault
                break;

            case 'searchAndReplace':
                onRender = onRenderSearchReplace
                break;
        }

        return <div>
            <Separator />
            {onRender()}
        </div>;
    }

    return (
        <Panel
            isOpen={props.isOpen}
            onDismiss={props.onCancel}
            type={PanelType.medium}
            closeButtonAriaLabel="Close"
            headerText="Bulk Costshare Edit"
            isLightDismiss={true}
            onRenderFooterContent={onRenderFooter}
            isFooterAtBottom={true}
        >
            {onRenderBody()}
        </Panel>
    )

}

function mapStateToProps(state: RootState) { return { state } }
const connector = connect(mapStateToProps);
type TComponentConnectedProps = ConnectedProps<typeof connector>;
export default connector(BulkCostShareWizard);

